/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: contact.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { FaLocationDot, FaPhone } from 'react-icons/fa6';
import { Button, ButtonGroup, Checkbox, Input, Textarea } from '@nextui-org/react';
import { motion } from 'framer-motion';
import { joiResolver } from '@hookform/resolvers/joi';
import emailjs from 'emailjs-com';
import Joi from 'joi';

import Assets from '@/constants/assets';
import Statics from '@/constants/statics';
import Stylings from '@/constants/stylings';
import BasicComponents from '@/shared/basic.components';
import Section from '@/shared/section';
import Utils from '@/utils';
/**************************************************************************************************************************
 TYPES / GLOBAL DEFINITIONS
***************************************************************************************************************************/
enum FormState {
  INITIAL = 0,
  IN_PROGRESS = 1,
  SUCCESS = 2,
  FAILED = 3
}

interface GetInTouchPayload {
  name: string;
  phone?: string;
  email: string;
  subject: string;
  message: string;
}
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const Contact: FunctionComponent = () => {
  const { t } = useTranslation('contact-us');

  const [formState, setFormState] = useState<FormState>(FormState.INITIAL);

  const [human, setHuman] = useState<boolean>(false);

  const [humanValidated, setHumanValidated] = useState<boolean | null>(null);

  const initialFormState: GetInTouchPayload = {
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  };

  const schema = Joi.object({
    name: Joi.string()
      .required()
      .messages({
        'string.empty': t('contact-form.form.errors.name.empty'),
        'any.required': t('contact-form.form.errors.name.required')
      }),
    phone: Joi.string().optional().allow(''),
    email: Joi.string()
      .required()
      .email({ tlds: { allow: false } })
      .messages({
        'string.empty': t('contact-form.form.errors.email.empty'),
        'any.required': t('contact-form.form.errors.email.required'),
        'string.email': t('contact-form.form.errors.email.invalid')
      }),
    subject: Joi.string()
      .required()
      .messages({
        'string.empty': t('contact-form.form.errors.subject.empty'),
        'any.required': t('contact-form.form.errors.subject.required')
      }),
    message: Joi.string()
      .required()
      .messages({
        'string.empty': t('contact-form.form.errors.message.empty'),
        'any.required': t('contact-form.form.errors.message.required')
      })
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset
  } = useForm<GetInTouchPayload>({
    defaultValues: initialFormState,
    resolver: joiResolver(schema)
  });

  const onHumanValueChange = (value: boolean) => {
    setHuman(value);
    if (value && !humanValidated) setHumanValidated(true);
  };

  const getPhone = (country: string) => {
    const index = Statics.PhoneItems.findIndex((item) => item.country === country);
    return Statics.PhoneItems[index];
  };

  const onSubmit = async (values: GetInTouchPayload): Promise<void> => {
    if (!human) {
      setHumanValidated(false);
      return;
    }

    setFormState(FormState.IN_PROGRESS);

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID as string,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID as string,
        { ...values },
        process.env.REACT_APP_EMAILJS_USER_ID as string
      )
      .then(
        ({ status }) => {
          if (status === 200) {
            localStorage.setItem('query_id', Utils.getUniqueId('user-query'));
            localStorage.setItem('query_timestamp', String(new Date().valueOf()));
            setFormState(FormState.SUCCESS);
          } else {
            setFormState(FormState.FAILED);
          }
        },
        () => setFormState(FormState.FAILED)
      );
  };

  const handleReset = (): void => {
    reset();
    setFormState(FormState.INITIAL);
  };

  useEffect(() => {
    if (localStorage.getItem('query_id')) {
      if (formState === FormState.INITIAL) {
        if (
          new Date().valueOf() - parseInt(localStorage.getItem('query_timestamp') as string, 10) <
          1 * 24 * 60 * 60 * 1000
        ) {
          setFormState(FormState.SUCCESS);
        } else {
          localStorage.removeItem('query_id');
          localStorage.removeItem('query_timestamp');
        }
      }
    }
  }, [formState]);

  return (
    <Section id="contact-us" className="bg-white">
      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="col-span-1 h-fit bg-themeBlue-900 flex flex-col items-center jsuitfy-start gap-10 p-5">
          <div className="col-span-1 flex flex-col items-center justify-start gap-5">
            <div className="w-full min-h-[40px] border-b-[1px] border-b-white flex flex-col items-center justify-center">
              <motion.p
                className="w-full text-lg text-white font-aldrich font-medium uppercase"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 100,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                {t('address.title')}
              </motion.p>
            </div>
            <ul className="w-full flex flex-col items-start justify-start gap-5">
              {Statics.AddressItems.map(({ id, country, address }, idx: number) => (
                <motion.li
                  key={id}
                  className="flex flex-col items-center justify-start gap-2"
                  initial="initial"
                  whileInView="entry"
                  viewport={{ once: true }}
                  variants={{
                    initial: {
                      x: 50 + idx * 10,
                      opacity: 0
                    },
                    entry: {
                      x: 0,
                      opacity: 1
                    }
                  }}
                  transition={{
                    duration: 0.5
                  }}
                >
                  <h5 className="w-full text-white font-medium">{country}</h5>
                  <a
                    className="w-full text-md text-white/60 hover:text-white flex items-center justify-start gap-[10px] transition-all duration-350"
                    href={`http://maps.google.com/?q=${address}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="w-[20px] h-[20px]">
                      <FaLocationDot />
                    </div>
                    <p className="w-[calc(100%-30px)]font-inter">{address}</p>
                  </a>
                  <a
                    className="w-full text-md text-white/60 hover:text-white flex items-center justify-start gap-[10px] transition-all duration-350"
                    href={getPhone(country).href}
                  >
                    <div className="w-[20px] h-[20px]">
                      <FaPhone />
                    </div>
                    <p className="w-[calc(100%-30px)]font-inter">{getPhone(country).label}</p>
                  </a>
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="w-full flex items-center justify-start gap-3">
            <motion.p
              className="text-lg text-white font-aldrich font-medium"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  x: 100,
                  opacity: 0
                },
                entry: {
                  x: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              {t('social.title')} -{' '}
            </motion.p>
            <div className="flex items-center justify-center gap-3">
              {Statics.SocialItems.map(({ id, icon: Icon, href }, idx: number) => (
                <motion.a
                  key={id}
                  className="w-[30px] h-[30px] bg-white/60 hover:bg-white text-purple-900 flex flex-col items-center justify-center rounded-full"
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  initial="initial"
                  whileInView="entry"
                  viewport={{ once: true }}
                  variants={{
                    initial: {
                      y: 50 + idx * 10,
                      opacity: 0
                    },
                    entry: {
                      y: 0,
                      opacity: 1
                    }
                  }}
                  transition={{
                    duration: 0.5
                  }}
                >
                  <Icon className="text" />
                </motion.a>
              ))}
            </div>
          </div>
          {/* <div className="w-full flex flex-col items-center justify-start gap-5">
            <div className="w-full min-h-[40px] border-b-[1px] border-b-white flex flex-col items-center justify-center">
              <motion.p
                className="w-full text-lg text-white font-aldrich font-medium uppercase"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 100,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                {t('business-hours.title')}
              </motion.p>
            </div>
            <div className="w-full flex flex-col items-center justify-start gap-2">
              {Statics.BusinessHours.map(({ id, day, timing }, idx: number) => (
                <motion.div
                  key={id}
                  className="w-full flex items-center justify-between bg-white/20 px-5 py-2"
                  initial="initial"
                  whileInView="entry"
                  viewport={{ once: true }}
                  variants={{
                    initial: {
                      y: -50 - idx * 10,
                      opacity: 0
                    },
                    entry: {
                      y: 0,
                      opacity: 1
                    }
                  }}
                  transition={{
                    duration: 0.5
                  }}
                >
                  <h6 className="text-left text-white font-inter font-medium">{day}</h6>
                  <p className="text-right text-white font-urbanist font-light">{timing}</p>
                </motion.div>
              ))}
            </div>
          </div> */}
        </div>
        <div className="col-span-1 lg:col-span-2 h-full flex flex-col items-start justify-start gap-10">
          <BasicComponents.SectionHeader
            title={t('contact-form.title')}
            subtitle={t('contact-form.subtitle')}
          />
          <motion.div
            className="w-full bg-themePurple-900 flex items-center justify-start gap-3 p-5"
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                x: -100,
                opacity: 0
              },
              entry: {
                x: 0,
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5
            }}
          >
            <p className="text-lg text-white font-inter font-light">
              {t('contact-form.estimation')} -{' '}
              <a
                className="text-white/60 hover:text-white font-aldrich transition-all duration-350"
                href={Statics.EmailItems[1].href}
              >
                {Statics.EmailItems[1].label}
              </a>
            </p>
          </motion.div>
          {formState === FormState.INITIAL && (
            <motion.div
              className="w-full flex flex-col items-center justify-start gap-5"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  y: -100,
                  opacity: 0
                },
                entry: {
                  y: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.name')}
                    placeholder={t('contact-form.form.placeholders.name')}
                    isInvalid={errors['name'] ? true : false}
                    errorMessage={errors['name']?.message}
                    maxLength={64}
                    {...field}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.email')}
                    placeholder={t('contact-form.form.placeholders.email')}
                    isInvalid={errors['email'] ? true : false}
                    errorMessage={errors['email']?.message}
                    maxLength={64}
                    {...field}
                  />
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.phone')}
                    placeholder={t('contact-form.form.placeholders.phone')}
                    maxLength={64}
                    {...field}
                  />
                )}
              />
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.subject')}
                    placeholder={t('contact-form.form.placeholders.subject')}
                    isInvalid={errors['subject'] ? true : false}
                    errorMessage={errors['subject']?.message}
                    maxLength={64}
                    {...field}
                  />
                )}
              />
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <Textarea
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.message')}
                    placeholder={t('contact-form.form.placeholders.message')}
                    isInvalid={errors['message'] ? true : false}
                    errorMessage={errors['message']?.message}
                    maxLength={256}
                    {...field}
                  />
                )}
              />
              <Checkbox
                key={String(human)}
                className="font-inter font-semibold"
                defaultChecked={human}
                checked={human}
                onValueChange={onHumanValueChange}
              >
                {t('contact-form.form.labels.human')}
                {!humanValidated && (
                  <span className="text-red-500 font-urbanist font-medium ml-2">
                    ({t('contact-form.form.errors.human.unchecked')})
                  </span>
                )}
              </Checkbox>
              <ButtonGroup>
                <Button
                  className="bg-themePurple-900/60 text-sm text-white hover:bg-themePurple-900 font-inter font-medium shadow-md drop-shadow-sm px-5 py-3"
                  radius="none"
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('contact-form.form.btns.submit')}
                </Button>
                <Button
                  className="bg-black/70 text-sm text-white hover:bg-black font-inter font-medium shadow-md drop-shadow-sm px-5 py-3"
                  radius="none"
                  onClick={handleReset}
                >
                  {t('contact-form.form.btns.reset')}
                </Button>
              </ButtonGroup>
            </motion.div>
          )}
          {formState === FormState.IN_PROGRESS && (
            <div className="w-full flex flex-col items-center justify-start gap-5">
              <img
                src={Assets.Images.IN_PROGRESS_GIF.src}
                alt={Assets.Images.IN_PROGRESS_GIF.alt}
                width={256}
                height={256}
              />
              <div className="w-full flex flex-col items-center justify-start gap-1">
                <h4 className="text-xl lg:text-3xl text-themeDarkBlue-900 font-aldrich font-bold">
                  {t('contact-form.states.in-progress.title')}
                </h4>
                <p className="text-md lg:text-lg text-black font-inter font-light">
                  {t('contact-form.states.in-progress.desc')}
                </p>
              </div>
            </div>
          )}
          {formState === FormState.FAILED && (
            <div className="w-full flex flex-col items-center justify-start gap-5">
              <img
                src={Assets.Images.FAILED.src}
                alt={Assets.Images.FAILED.alt}
                width={256}
                height={256}
              />
              <div className="w-full flex flex-col items-center justify-start gap-1">
                <h4 className="text-xl lg:text-3xl text-themeDarkBlue-900 font-aldrich font-bold">
                  {t('contact-form.states.failed.title')}
                </h4>
                <p className="text-md lg:text-lg text-black font-inter font-light">
                  {t('contact-form.states.failed.desc')}
                </p>
              </div>
            </div>
          )}
          {formState === FormState.SUCCESS && (
            <div className="w-full flex flex-col items-center justify-start gap-5">
              <img
                src={Assets.Images.SUCCESS.src}
                alt={Assets.Images.SUCCESS.alt}
                width={256}
                height={256}
              />
              <div className="w-full flex flex-col items-center justify-start gap-1">
                <h4 className="text-xl lg:text-3xl text-themeDarkBlue-900 font-aldrich font-bold">
                  {t('contact-form.states.success.title')}
                </h4>
                <p className="text-md lg:text-lg text-black font-inter font-light">
                  {t('contact-form.states.success.desc')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default Contact